import jQuery from 'jquery';
import {Promise, Thenable} from "es6-promise";
import {inspect} from "util";
import Alerts from "./misc/alerts";
import PlainObject = JQuery.PlainObject;
import StackTrace from "stacktrace-js";
import {Spinner} from "./views/spinner";
import {User} from "./bo/user";
import {Attachment} from "./bo/attachment";
import {Uploader} from "./misc/images";
import {ChangePassword} from "./bo/change_password";
import {Recording} from "./bo/recording";
import {ShopItem} from "./bo/shop_item";
import {Balance} from "./bo/balance";
import {JsonDate} from "./misc/types";
import {S3} from "./misc/s3";

/**
 * @return true if error has been handled, false to permit normal error handling
 */
type ErrorCallback = (msg: any) => boolean;

export interface RequestOptions {
    silent?: boolean,
    isLogin?: boolean,
    noAuthRedirects?: boolean;
    errorCallback?: ErrorCallback,
    noCache?: boolean,
    cachePrefix?: string,
    browserCaching?: boolean,
    timeoutMs?: number,
    uploadProgressListener?: (event: ProgressEvent<XMLHttpRequestEventTarget>) => any
}

export const CLEAR_CACHE = "clear_cache";

const VOLATILE = "volatile";

function encodePathComponent(x: string) : string {
    // Tomcat chokes on even url-encoded slashes in path components.
    // So as a workaround, we transport slashes as <something else>.
    // This means that we can't represent a the <something else> in a URL in this system,
    // but we are very unlikely to want to and it's a much smaller problem than not being able
    // to represent a slash...
    const y = x && x.replace ? x.replace("/", "<<SLASH>>") : x;
    return encodeURIComponent(y);
}

export function getAppUrl() {
    const href = window.location.href;
    const index = href.indexOf("#");
    return index == -1 ? href : href.substr(0, index);
}

function paramsToQueryString(params: any) {
    const keys = Object.keys(params);
    if (keys.length == 0) {
        return "";
    }

    return "?" + keys.map((key) => key + "=" + encodeURIComponent(params[key])).join("&");
}

/**
 * Models interactions with the server, handles caching
 */
export default class Server {

    /**
     * Singleton instance
     */
    private static INSTANCE: Server;

    static instance() {
        if (Server.INSTANCE == null) {
            Server.INSTANCE = new Server();
        }
        return Server.INSTANCE;
    }

    private resolveUser? : (value?: User | Thenable<User>) => void;

    private user_ = new Promise<User>((resolve, reject) => {
        this.resolveUser = resolve;
    });

    public setUser(user: User) {
        this.resolveUser && this.resolveUser(user);
    }

    public getUser() : Promise<User> {
        return this.user_;
    }

    /**
     * Private cache of GET responses.
     */
    private cache : {
        [key: string] : Promise<any>
    } = {};

    public runtimeParams_ : Promise<PlainObject>;

    private runtimeParamsResolver?: (value?: PlainObject | Thenable<PlainObject>) => void;

    private defaultOpts : RequestOptions = {
        noAuthRedirects: false
    };

    private constructor() {
        this.runtimeParams_ = new Promise((resolve, reject) => {
            this.runtimeParamsResolver = resolve;
            this.ping("params"); // side effect is to populate runtime params
        });

        this.runtimeParams_.then((params) => {
            if (params["wrb.client.noAuthRedirects"]) {
                this.defaultOpts.noAuthRedirects = true;
            }
        });
    }

    /**
     * Set default request options
     * @param opts
     * @return previous default request options
     */
    setRequestOpts(opts: RequestOptions) : RequestOptions {
        const r = this.defaultOpts;
        this.defaultOpts = opts;
        return r;
    }

    ping(msg: string) : Promise<PingResult> {

        // @ts-ignore
        const clientVersion : PingResult = (window && window.wrb_git) || {
            "version": "?",
            "branch": "?"
        };

        const l = calcLocalStorageSize();

        const data = {
            msg: msg,
            client: clientVersion,
            ls: l.size,
            lc: l.cnt
        };

        const ping_ = this.ajax({
            method: "GET",
            url: "api/ping",
            dataType: "json",
            data: data,
            contentType: "application/json; charset=utf-8",
        }, {
            silent: true
        });

        ping_.then((ping) => {
            // capture client-side params
            if (ping && ping.params) {
                this.runtimeParamsResolver && this.runtimeParamsResolver(ping.params);
            }
        });

        return ping_;
    }

    loginRemembered() : Promise<User | undefined> {
        return this.ajax({
            method: "GET",
            url: "api/login"
        });
    }

    /**
     * @return true if user didn't exist (ie, new signup)
     */
    signup(data: { given_name?: string, family_name?: string, email?: string }) : Promise<boolean> {
        return this.ajax({
            method: "POST",
            url: "api/signup",
            data: JSON.stringify(data),
            contentType: "application/json; charset=utf-8",
        });
    }

    login(data: { username?: string, password?: string }) : Promise<User> {
        return this.ajax({
            method: "POST",
            url: "api/login",
            data: JSON.stringify(data),
            contentType: "application/json; charset=utf-8",
        });
    }

    logout() : Promise<any> {
        return this.ajax({
            method: "POST",
            url: "api/logout",
            contentType: "application/json; charset=utf-8",
        });
    }

    changePassword(data: ChangePassword) : Promise<boolean> {
        return this.ajax({
            method: 'POST',
            url: "api/security/change-password",
            data: JSON.stringify(data),
            contentType: "application/json; charset=utf-8",
        });
    }

    getUsers() : Promise<User[]> {
        return this.ajax({
            method: "GET",
            url: `api/users`,
            contentType: "application/json; charset=utf-8",
        });
    }

    saveUser(user: User) : Promise<User[]> {
        return this.ajax({
            method: "POST",
            url: `api/users`,
            data: JSON.stringify(user),
            contentType: "application/json; charset=utf-8"
        });
    }

    uploadFile(file: File) : Promise<Attachment> {
        const uploader = new Uploader({
            url: "api/docs",
            maxWidth: 800,
            maxHeight: 800
        }, this);
        return uploader.uploadFile(file);
    }

    uploadToS3(file: File, listener: (status: string) => any) : Promise<string> {
        const uploader = new S3(this, listener);
        return uploader.uploadFile(file);
    }

    fetchLibraries() : Promise<string[]> {
        return this.ajax({
            method: "GET",
            url: `api/libraries`,
            contentType: "application/json; charset=utf-8",
        });
    }

    fetchRecordings(lib: string) : Promise<Recording[]> {
        return this.ajax({
            method: "GET",
            url: `api/recordings?library=${encodeURIComponent(lib)}`,
            contentType: "application/json; charset=utf-8",
        });
    }

    fetchRecording(id: number) : Promise<Recording> {
        return this.ajax({
            method: "GET",
            url: `api/recordings/${id}`,
            contentType: "application/json; charset=utf-8",
        });
    }

    fetchRecordingByToken(token: string) : Promise<Recording> {
        return this.ajax({
            method: "GET",
            url: `api/recordings/token/${token}`,
            contentType: "application/json; charset=utf-8",
        });
    }

    saveRecording(recording: Recording, main_url: string, sample_url: string) : Promise<boolean> {
        recording.url = main_url;
        // @ts-ignore
        recording["sample_url"] = sample_url;
        return this.ajax({
            method: "POST",
            url: `api/recordings`,
            data: JSON.stringify(recording),
            contentType: "application/json; charset=utf-8",
        });
    }

    // -------------------------------

    fetchShopCatalogue() : Promise<ShopItem[]> {
        return this.ajax({
            method: "GET",
            url: `api/shop/catalogue`,
            contentType: "application/json; charset=utf-8",
        });
    }

    createPaymentIntent(cents: number, description?: string | undefined) : Promise<string> {
        const data : any = {
            cents: cents
        };
        if (description) {
            data.description = description;
        }
        return this.ajax({
            method: "POST",
            url: `api/payments/intent`,
            data: JSON.stringify(data),
            contentType: "application/json; charset=utf-8"
        });
    }

    purchase(paymentIntent: any) : Promise<any> {
        return this.ajax({
            method: "POST",
            url: `api/shop/purchase`,
            data: JSON.stringify(paymentIntent),
            contentType: "application/json; charset=utf-8"
        });
    }

    getBalance() : Promise<Balance> {
        return this.ajax({
            method: "GET",
            url: `api/balance`,
            contentType: "application/json; charset=utf-8"
        }, {
            noAuthRedirects: true
        })
    }

    getPractichordToken() : Promise<any> {
        return this.ajax({
            method: "GET",
            url: `api/practichord`,
            contentType: "application/json; charset=utf-8"
        })
    }

    addRecording(r: Recording) : Promise<any> {
        return this.ajax({
            method: "POST",
            url: `api/shop/add-recording`,
            data: JSON.stringify({id: r.id}),
            contentType: "application/json; charset=utf-8"
        })
    }

    saveMarks(r: Recording, marks: number[]) : Promise<number[]> {
        return this.ajax({
            method: "POST",
            url: `api/recordings/${r.id}/marks`,
            data: JSON.stringify(marks),
            contentType: "application/json; charset=utf-8"
        }, {
            silent: true
        });
    }

    saveRepitch(r: Recording, cents: number) : Promise<number[]> {
        return this.ajax({
            method: "POST",
            url: `api/recordings/${r.id}/repitch`,
            data: JSON.stringify({cents: cents}),
            contentType: "application/json; charset=utf-8"
        });
    }

    setActivation(r: Recording, active: boolean) : Promise<any> {
        return this.ajax({
            method: "POST",
            url: `api/recordings/${r.id}/activation`,
            data: JSON.stringify({active: active}),
            contentType: "application/json; charset=utf-8"
        });
    }

    setActivationByToken(token: string, active: boolean) : Promise<any> {
        return this.ajax({
            method: "POST",
            url: `api/recordings/token/${token}/activation`,
            data: JSON.stringify({active: active}),
            contentType: "application/json; charset=utf-8"
        });
    }

    getFakebookUrl() : Promise<string | false> {
        return this.getUser().then(user => {
            if (! user || ! user.username) {
                return false;
            }
            return `api/fakebook?${encodeURIComponent(user.username)}`;
        });
    }

    // -------------------------------

    getSalesSummaryReport(from: JsonDate, to: JsonDate) : Promise<any> {
        const data = {
            from: from,
            to: to
        };
        return this.ajax({
            method: "GET",
            url: `api/reports/sales/summary`,
            data: data,
            contentType: "application/json; charset=utf-8"
        });
    }

    getSalesDetailsReport(from: JsonDate, to: JsonDate) : Promise<any> {
        const data = {
            from: from,
            to: to
        };
        return this.ajax({
            method: "GET",
            url: `api/reports/sales/details`,
            data: data,
            contentType: "application/json; charset=utf-8"
        });
    }

    getSalesDetailsCsvUrl(from: JsonDate, to: JsonDate) : string {
        return `api/reports/sales/details.csv?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    }
    // -------------------------------

    sendReset(username: string) : Promise<any> {
        return this.ajax({
            method: "POST",
            url: `api/security/send-reset`,
            data: JSON.stringify(username),
            contentType: "application/json; charset=utf-8"
        });
    }

    // ------------------------------
    // debugging

    log(msg: string) : Promise<any> {
        console.log(msg);
        return this.ajax({
            method: "POST",
            url: "json/log",
            data: msg
        }, {
            silent: true
        });
    }

    trace(msg: string) {
        const stack_ = StackTrace.get();
        stack_.then((stack) => {

            for (const line of stack) {
                msg = msg + "\n  " + line.toString()
            }

            console.log("------------------------");
            console.log(msg);
            console.log("------------------------");

            this.log(msg);
        });
    }

    // ------------------------------
    // Misc.

    /**
     * Returns absolute URL, base of server app
     * This works for the web app and the mobile app.
     */
    getBaseUrl() : string {
        // @ts-ignore
        const url = window.location.href as string;

        // Remove hash part and trailing slash, any query string
        let baseUrl = url
            .replace(/#.*$/, "")
            .replace(/\?.*$/, "")
            .replace(/\/(([^\/\.]+\.[^\/\.]+))?$/, "");

        return baseUrl;
    }

    getShortBaseUrl() : Promise<string> {
        return this.runtimeParams_.then((params) => {
            return params["wrb.client.short_url"];
        });
    }

    /**
     * Wrap ajax calls so we can intercept if necessary.
     * @param settings
     */
    ajax(settings: JQuery.AjaxSettings, opts?: RequestOptions) : Promise<any> {

        const options : RequestOptions = jQuery.extend({}, this.defaultOpts, opts || {});

        const l = options.uploadProgressListener;
        if (l) {
            settings.xhr = () => {
                const xhr = new XMLHttpRequest();
                xhr.upload.addEventListener("progress", l);
                return xhr;
            }
        }

        // @ts-ignore
        const baseurl = this.getBaseUrl();

        const method = settings.method || "GET";
        const url = settings.url;

        const absUrl = baseurl + "/" + url;

        // Don't cache if settings.data is set (ie, query string)
        const useCache = method == "GET" && url && ! settings.data && ! options.noCache && url !== "json/ping" && url.indexOf("?") == -1;

        const cacheKey = (options.cachePrefix ? options.cachePrefix + ":" + url : url) as string;

        if (useCache) {
            // check cache
            const cached = this.cache[cacheKey];
            if (cached) {
                // this.debug(`Server cache: ${method} ${url}`);
                return cached;
            }
        }

        // @ts-ignore
        const isMobile = window.dk_mobile;
        const useOfflineCache = useCache && isMobile;

        if (method == "POST") {
            this.clearCache();
        } else {
            settings.cache = false;
        }

        // this.debug(`Server fetch: ${method} ${absUrl}`);

        // const ctx = this.ctx;

        // idempotence - for payment required retries
        if (url && url.indexOf(baseurl) != 0) {
            settings.url = absUrl;
        }

        const start = Date.now();
        const cache = this.cache;
        let counter = 0;

        const popSpinner = options.silent ? () => {} : Spinner.pushSpinner();

        const p = new Promise((resolve, reject) => {

            sendData.apply(this);

            function sendData(this: Server) {

                const id = ++counter;

                settings.xhrFields = {
                    withCredentials: true
                };

                if (opts && opts.timeoutMs !== undefined) {
                    settings.timeout = opts.timeoutMs;
                }

                // Aggressive anti-caching to overcome Internet Explorer's aggressive caching
                settings.cache = options.browserCaching;

                const xhr = jQuery.ajax(settings);

                xhr.then((data, textStatus, response) => {
                    const delay = Date.now() - start;
                    // console.log(`Server received ${url} after ${delay} ms`);
                    const update = response.getResponseHeader("X-wrb-update");
                    // @ts-ignore
                    const update0 = window.wrb_git && window.wrb_git.update;
                    if (update && update0 != update) {
                        jQuery("body").addClass("update-pending");
                    }
                    resolve(data);
                });

                xhr.fail((data) => {

                    if (options.silent) {
                        reject("silent error");
                        return;
                    }

                    console.log("sendData fail " + id);

                    let error = undefined;
                    let reload = false;

                    console.log("Ajax fail, readyState=" + data.readyState + ", status=" + data.status + " " + data.statusText + ", URL=" + url);
                    console.log("Ajax fail data: " + inspect(data));
                    this.trace("Ajax fail trace: ");

                    if (data.readyState == 0) {

                        // Network error...
                        // Consider retrying request if its a GET and not too long has elapsed since we started.
                        const retry = method == "GET" && (Date.now() - start) < 30 * 1000 && counter < 30;

                        if (retry) {
                            setTimeout(() => {
                                console.log("Retrying... " + id);
                                sendData.apply(this);
                            }, 1000);

                            // Leave fail handler, without resolving outer promise.
                            xhr.abort();
                            return;
                        }

                        // Network error. We could put some retry logic in here
                        error = "There is a problem with the network or the server. Please try again in a few minutes";

                    } else if (data.status == 401) {
                        if (! options || ! options.noAuthRedirects) {
                            // Login required
                            reload = true;
                        }
                    } else {
                        let handled = false;
                        if (options.errorCallback && data.responseJSON) {
                            handled = options.errorCallback(data.responseJSON)
                        }
                        if (! handled) {
                            if (data.responseJSON && data.responseJSON.error) {
                                error = data.responseJSON.error;
                            } else {
                                // Unknown error
                                error = "An unknown error has ocurred...";
                            }
                        }
                    }

                    if (reload) {
                        window.location.href = "#/login";
                        window.location.reload();
                    }

                    if (error) {
                        this.error(error);
                    }

                    reject(error);

                });
            }

        });

        if (useCache) {
            cache[cacheKey] = p;
        }

        p.finally(() => {
            popSpinner();
        });

        return p;
    }

    private error(msg: string[] | string) {
        // Display directly. This could be refactored out of the server class, eg via events.

        console.log("ERROR", msg);

        if (typeof msg == "string") {
            msg = [ msg ];
        }

        // Format array of messages into HTML
        const ol = jQuery("<ol>").addClass("server-error-list");
        jQuery(msg).each((idx) => {
            jQuery("<li>").text(msg[idx]).appendTo(ol);
        });

        Alerts.alert({
            messageHTML: ol[0].outerHTML
        });

    }

    qa(err: string) {
        const id = Date.now();
        this.ajax({
            method: 'POST',
            url: "api/qa/" + id,
            data: err
        }, {
            silent: true
        });
        return id;
    }

    debug(msg: any) {
        console.log(msg);
    }

    /**
     * Clear the local cache (not the offline cache)
     */
    public clearCache(ts?: number) {
        const ts2 = ts || Date.now();
        const cachesize = Object.keys(this.cache).length;

        console.log("clearCache ts=" + ts + "; cachesize=" + cachesize);

        // Crude - invalidate entire cache
        this.cache = {};

        // if we are the originator, trigger cross-tabs refresh with same value
        if (! ts) {
            window.localStorage.setItem(CLEAR_CACHE, ts2.toString());
        }
    }

    public clearCachePrefix(prefix: string) {
        console.log("clearCacheclearCachePrefix " + prefix);
        const keys = Object.keys(this.cache).filter((key) => key.indexOf(prefix) == 0);
        for (const key of keys) {
            console.log("clearCacheclearCachePrefix => " + key);
            delete this.cache[key];
        }
    }

    public clearVolatileCache() {
        this.clearCachePrefix(VOLATILE);
    }

    // ------------------------------

    /**
     * Payment nonces are transported as http headers along with the business payload in the http body.
     */
    private addPaymentNonce(settings: JQuery.AjaxSettings, paymentNonce: string | undefined) {
        if (paymentNonce == undefined) {
            return;
        }
        const headers = settings.headers || {};
        headers["X-TP-payment-nonce"] = paymentNonce;
        settings.headers = headers;
    }

    /**
     * A "dry run" can be executed by adding a special HTTP header to post requests
     */
    private dryrun(settings: JQuery.AjaxSettings) {
        const headers = settings.headers || {};
        headers["X-TP-dryrun"] = "true";
        settings.headers = headers;
    }

}

export interface PingResult {
    version?: string;
    branch?: string;
    commit?: string;
    playstoreUrl?: string;
}

function calcLocalStorageSize() {
    let size = 0;
    let cnt = 0;

    for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key != null) {
            const val = window.localStorage[key];
            cnt ++;
            if (val && val.length) {
                size += val.length;
            }
        }
    }
    return {
        size: size,
        cnt: cnt
    };
}

export function localStoragePrefix() {
    return "talc-";
}
